import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SophisticatedCarousel from "@/components/SophisticatedCarousel";
import { ArrowRight, Globe, TrendingUp, Users, Shield, Building, Heart, Award, MapPin, Briefcase } from "lucide-react";
import { useEffect, useRef } from "react";

const Brazil = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  const brazilCarouselImages = [
    "./images/CARROSSEL 01.jpg",
    "./images/CARROSSEL 02.jpg", 
    "./images/CARROSSEL 03.jpg",
    "./images/CARROSSEL 04.jpeg",
    "./images/CARROSSEL 05.jpg",
    "./images/CARROSSEL 06.png",
    "./images/CARROSSEL 07.jpg",
    "./images/CARROSSEL 08.jpg",
    "./images/CARROSSEL 09.jpg"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section with Parallax Carousel */}
      <section className="relative">
        <SophisticatedCarousel 
          images={brazilCarouselImages}
          interval={6000}
          className="h-screen"
        />
        
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="elite-container text-center text-white">
            <div className="fade-in">
              <h1 className="font-luxury text-6xl lg:text-8xl font-bold mb-8 leading-tight text-shadow-strong">
                Brazil: A Global
                <span className="block text-accent">Safe Harbor</span>
              </h1>
              <p className="text-2xl lg:text-4xl mb-12 max-w-6xl mx-auto leading-relaxed text-shadow-strong">
                The largest African nation outside of Africa.<br />
                Your gateway to G20 prosperity and family legacy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Economic Powerhouse */}
      <section className="elite-section bg-white">
        <div className="elite-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="fade-in">
              <h2 className="elite-heading">Economic Powerhouse</h2>
              <div className="elite-divider"></div>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Brazil stands as the <strong>9th largest economy globally</strong> and the undisputed 
                leader of Latin America. As a founding member of the G20, Brazil offers unparalleled 
                institutional stability and robust legal frameworks for international investors.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-4 h-4 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h3 className="font-display text-xl text-primary mb-2">G20 Member Nation</h3>
                    <p className="text-slate-600">Institutional stability and international recognition</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-4 h-4 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h3 className="font-display text-xl text-primary mb-2">$2.6 Trillion GDP</h3>
                    <p className="text-slate-600">Largest economy in Latin America with diverse sectors</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-4 h-4 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h3 className="font-display text-xl text-primary mb-2">Continental Scale</h3>
                    <p className="text-slate-600">5th largest country by area with abundant natural resources</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative fade-in">
              <img 
                src="./images/ChatGPT Image 4 de fev. de 2026, 22_32_41.png" 
                alt="Brazil's economic growth with city skyline and financial charts" 
                className="rounded-lg shadow-2xl w-full elite-hover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Cultural Connection */}
      <section className="elite-section bg-slate-50">
        <div className="elite-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="relative fade-in order-2 lg:order-1">
              <img 
                src="./images/ChatGPT Image 4 de fev. de 2026, 22_45_23.png" 
                alt="African cultural heritage in Brazil" 
                className="rounded-lg shadow-2xl w-full elite-hover"
              />
            </div>
            <div className="fade-in order-1 lg:order-2">
              <h2 className="elite-heading">The Largest African Nation Outside Africa</h2>
              <div className="elite-divider"></div>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Brazil is home to the <strong>largest African diaspora</strong> outside the African continent, 
                offering a unique environment of cultural familiarity, culinary heritage, and social integration 
                that makes Nigerian investors feel truly at home.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <Heart className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-display text-xl text-primary mb-2">Cultural Affinity</h3>
                    <p className="text-slate-600">Deep African cultural roots in music, cuisine, and traditions</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Users className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-display text-xl text-primary mb-2">Social Integration</h3>
                    <p className="text-slate-600">Welcoming society with established African communities</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Globe className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-display text-xl text-primary mb-2">Language Bridge</h3>
                    <p className="text-slate-600">Portuguese language opens doors to global opportunities</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Markets */}
      <section className="elite-section bg-white">
        <div className="elite-container">
          <div className="text-center mb-20 fade-in">
            <h2 className="elite-heading">Strategic Investment Markets</h2>
            <div className="elite-divider"></div>
            <p className="elite-subheading mx-auto">
              Focus on Brazil's most secure and prestigious markets: Brasília for institutional stability 
              and São Paulo for economic dynamism.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Brasília */}
            <Card className="elite-card fade-in elite-hover">
              <CardContent className="p-12">
                <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mb-8">
                  <Building className="h-10 w-10 text-accent" />
                </div>
                <h3 className="font-display text-3xl text-primary mb-6">Brasília</h3>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  Brazil's capital city offers unmatched <strong>institutional stability</strong>, 
                  world-class infrastructure, and the highest concentration of government and 
                  diplomatic activities in Latin America.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                    <span className="text-slate-600">UNESCO World Heritage Site</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                    <span className="text-slate-600">Highest HDI in Brazil</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                    <span className="text-slate-600">Political and diplomatic center</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* São Paulo */}
            <Card className="elite-card fade-in elite-hover">
              <CardContent className="p-12">
                <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mb-8">
                  <TrendingUp className="h-10 w-10 text-accent" />
                </div>
                <h3 className="font-display text-3xl text-primary mb-6">São Paulo</h3>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  Latin America's <strong>financial capital</strong> and Brazil's economic powerhouse, 
                  offering the highest concentration of multinational corporations, banks, 
                  and investment opportunities.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                    <span className="text-slate-600">Financial capital of Latin America</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                    <span className="text-slate-600">Largest stock exchange in region</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                    <span className="text-slate-600">Global business hub</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Family Reunification */}
      <section className="elite-section bg-slate-50">
        <div className="elite-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="fade-in">
              <h2 className="elite-heading">Family Reunification Rights</h2>
              <div className="elite-divider"></div>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Brazilian law <strong>guarantees</strong> the right to Family Reunification, allowing 
                your investment to protect not just your assets, but your entire family's future 
                with full rights to residence, education, healthcare, and business opportunities.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <Shield className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-display text-xl text-primary mb-2">Legal Guarantee</h3>
                    <p className="text-slate-600">Constitutional right to bring spouse and dependents</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Users className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-display text-xl text-primary mb-2">Full Rights</h3>
                    <p className="text-slate-600">Access to education, healthcare, and employment</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Heart className="h-6 w-6 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-display text-xl text-primary mb-2">Family Legacy</h3>
                    <p className="text-slate-600">Multi-generational wealth and citizenship pathway</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative fade-in">
              <img 
                src="./images/ChatGPT Image 4 de fev. de 2026, 23_00_24.png" 
                alt="Nigerian family enjoying their new life in Brazil" 
                className="rounded-lg shadow-2xl w-full elite-hover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Legal Pathway */}
      <section className="elite-section bg-white">
        <div className="elite-container">
          <div className="text-center mb-20 fade-in">
            <h2 className="elite-heading">Clear Legal Pathway</h2>
            <div className="elite-divider"></div>
            <p className="elite-subheading mx-auto">
              Urban real estate investment provides a transparent, established route to Permanent Residency 
              and eventual Brazilian citizenship with complete legal security and government backing.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center fade-in">
              <div className="w-24 h-24 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-8">
                <Briefcase className="h-12 w-12 text-accent" />
              </div>
              <h3 className="font-display text-2xl text-primary mb-6">Investment</h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                Strategic real estate investment in premium Brazilian markets with full legal compliance.
              </p>
            </div>
            
            <div className="text-center fade-in">
              <div className="w-24 h-24 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-8">
                <Shield className="h-12 w-12 text-accent" />
              </div>
              <h3 className="font-display text-2xl text-primary mb-6">Residency</h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                Permanent Residency status with full rights and protections under Brazilian law.
              </p>
            </div>
            
            <div className="text-center fade-in">
              <div className="w-24 h-24 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-8">
                <Award className="h-12 w-12 text-accent" />
              </div>
              <h3 className="font-display text-2xl text-primary mb-6">Citizenship</h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                Pathway to Brazilian citizenship and one of the world's most powerful passports.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-screen">
        <SophisticatedCarousel 
          images={[
            "./images/ChatGPT Image 4 de fev. de 2026, 23_03_05.png",
            "./images/ChatGPT Image 4 de fev. de 2026, 22_57_59.png",
            "./images/ChatGPT Image 4 de fev. de 2026, 23_12_24.png"
          ]}
          interval={6000}
          className="absolute inset-0"
        />
        <div className="relative z-10 elite-container text-center text-white h-full flex items-center justify-center">
          <div className="fade-in">
            <h2 className="font-luxury text-5xl lg:text-6xl font-bold mb-8 text-shadow-strong">
              Your Brazilian Legacy
              <span className="block text-accent">Starts Today</span>
            </h2>
            <div className="elite-divider bg-accent"></div>
            <p className="text-2xl lg:text-3xl mb-12 max-w-5xl mx-auto leading-relaxed text-shadow-strong">
              Discover how Brazil's unique combination of economic strength, cultural affinity, 
              and family-focused immigration policies can secure your family's future.
            </p>
            <Button asChild className="elite-button text-xl px-16 py-6 shadow-2xl">
              <Link to="/contact">
                Schedule Your Private Consultation
                <ArrowRight className="ml-2 h-6 w-6" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Brazil;